import type { ReactNode } from 'react';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import { maskFieldValue } from '@ooh/ui/components/masked-value';
import {
  getOfferMediaUnitFields,
  type OfferMediaUnitField,
  type OfferMediaUnitFieldId,
  type OfferMediaUnitSurface,
} from './offer-media-unit-fields';
import {
  renderMediaUnitArea,
  renderMediaUnitAudience,
  renderMediaUnitBoolean,
  renderMediaUnitDimensions,
  renderMediaUnitFormat,
  renderMediaUnitGrwId,
  renderMediaUnitNoPhoto,
  renderMediaUnitNoPhotoHeader,
  renderMediaUnitTags,
  renderMediaUnitText,
} from './media-unit-field-renderers';

export interface MediaUnitFieldRowAdapter<Row> {
  getValue: (row: Row, fieldId: OfferMediaUnitFieldId) => unknown;
  getDimensionNotes: (row: Row) => unknown;
  getFormatLabel: (row: Row) => unknown;
  /**
   * Row-level supplier-withheld marker (field-visibility) — decides masking
   * for the supplier.view-gated `supplier`/`supplierNumber` columns only.
   * Never derived from a caller-level permission flag, and never from the
   * row's cost-withheld marker (field-visibility: "The two permissions are
   * resolved independently") — this builder has no cost-gated fields, so it
   * never reads a cost marker.
   */
  getSupplierWithheld: (row: Row) => boolean;
}

export interface MediaUnitFieldColumnLabels {
  columns: Readonly<Record<string, string>>;
  trueLabel: string;
  falseLabel: string;
  noPhotoAria: string;
}

interface BuildMediaUnitFieldColumnsOptions<Row> {
  surface: OfferMediaUnitSurface;
  adapter: MediaUnitFieldRowAdapter<Row>;
  labels: MediaUnitFieldColumnLabels;
  /**
   * Shown on a masked supplier.view-gated cell (field-visibility). The
   * `supplier`/`supplierNumber` columns stay present for every caller;
   * only their cells mask, decided per row from the row's own withheld
   * marker.
   */
  supplierMaskedExplanation: string;
  sortKeys?: Readonly<Record<string, string>>;
  renderCellOverrides?: Readonly<Record<string, (row: Row) => ReactNode>>;
}

const RIGHT_ALIGNED_COLUMN_IDS = new Set(['area', 'audienceEstimate']);
const DEFAULT_COLUMN_WIDTHS: Readonly<Record<string, number>> = {
  mediaType: 130,
  format: 110,
  supplier: 140,
  supplierNumber: 130,
  grwId: 130,
  region: 130,
  county: 120,
  agglomeration: 140,
  city: 120,
  address: 190,
  dimensions: 135,
  audienceEstimate: 130,
  notes: 200,
  illuminated: 105,
  material: 120,
  multiformat: 110,
  tags: 160,
  roadNumber: 120,
  area: 110,
};

/**
 * Renders the columns whose cell needs field-specific formatting (not the
 * generic masked-text fallback) — extracted out of `renderCell` below (via a
 * `switch` instead of an if-chain) purely to stay within the file's
 * `complexity` budget. Returns `undefined` for any columnId with no special
 * renderer, signaling the caller to fall through to the generic text cell.
 */
function renderSpecialFieldCell<Row>(
  columnId: string,
  row: Row,
  field: OfferMediaUnitField,
  adapter: MediaUnitFieldRowAdapter<Row>,
  labels: MediaUnitFieldColumnLabels,
): ReactNode | undefined {
  switch (columnId) {
    case 'dimensions':
      return renderMediaUnitDimensions(
        adapter.getValue(row, 'width'),
        adapter.getValue(row, 'height'),
        adapter.getDimensionNotes(row),
      );
    case 'audienceEstimate':
      return renderMediaUnitAudience(adapter.getValue(row, 'audience'));
    case 'format':
      return renderMediaUnitFormat(
        adapter.getValue(row, 'formatCode'),
        adapter.getFormatLabel(row),
      );
    case 'grwId':
      return renderMediaUnitGrwId(adapter.getValue(row, field.id));
    case 'noPhoto':
      return renderMediaUnitNoPhoto(adapter.getValue(row, field.id) === false, labels.noPhotoAria);
    case 'area':
      return renderMediaUnitArea(adapter.getValue(row, field.id));
    case 'illuminated':
      return renderMediaUnitBoolean(adapter.getValue(row, 'lighting'), labels);
    case 'tags':
      return renderMediaUnitTags(adapter.getValue(row, 'tags'));
    default:
      return undefined;
  }
}

function getColumnPresentation(columnId: string, labels: MediaUnitFieldColumnLabels) {
  if (columnId === 'noPhoto') {
    const label = labels.columns[columnId] ?? columnId;
    return {
      label: '',
      toggleLabel: label,
      renderHeader: () => renderMediaUnitNoPhotoHeader(label),
      align: 'center' as const,
      width: 36,
    };
  }
  return {
    label: labels.columns[columnId] ?? columnId,
    align: RIGHT_ALIGNED_COLUMN_IDS.has(columnId) ? ('right' as const) : ('left' as const),
    width: DEFAULT_COLUMN_WIDTHS[columnId] ?? 120,
  };
}

export function buildMediaUnitFieldColumns<Row>({
  surface,
  adapter,
  labels,
  supplierMaskedExplanation,
  sortKeys = {},
  renderCellOverrides = {},
}: BuildMediaUnitFieldColumnsOptions<Row>): ColumnDef<Row>[] {
  // Every field stays present regardless of the caller's permission — a
  // supplier.view-gated field masks its own cell instead of the column
  // being omitted (field-visibility: "Restricted values are masked, not
  // removed from the layout").
  const fields = getOfferMediaUnitFields(surface);
  const uniqueColumnIds = [...new Set(fields.map((field) => field.columnId))];

  return uniqueColumnIds.map((columnId) => {
    const field = fields.find((candidate) => candidate.columnId === columnId);
    if (!field) throw new Error(`Offer media-unit field missing for column ${columnId}`);

    return {
      id: columnId,
      ...getColumnPresentation(columnId, labels),
      sortable: field.sortable,
      sortKey: sortKeys[columnId] ?? columnId,
      renderCell: (row) => {
        const override = renderCellOverrides[columnId];
        if (override) return override(row);
        const special = renderSpecialFieldCell(columnId, row, field, adapter, labels);
        if (special !== undefined) return special;
        const value = adapter.getValue(row, field.id);
        const rendered = renderMediaUnitText(value);
        if (field.gate !== 'supplier.view') return rendered;
        return maskFieldValue(
          value,
          adapter.getSupplierWithheld(row),
          supplierMaskedExplanation,
          rendered,
        );
      },
    };
  });
}
